// @mui
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Logo from 'src/components/logo';
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

const FEATURES = [
  { icon: 'solar:users-group-rounded-bold', label: 'Manage agencies & CRM users' },
  { icon: 'solar:chart-2-bold', label: 'Real-time analytics & reporting' },
  { icon: 'solar:shield-keyhole-bold', label: 'Granular permission controls' },
  { icon: 'solar:settings-bold', label: 'Full platform configuration' },
];

export default function Layout({ children }) {
  const upMd = useResponsive('up', 'md');

  const renderSection = (
    <Stack
      sx={{
        flexGrow: 1,
        p: { md: 7 },
        justifyContent: 'space-between',
        background: 'linear-gradient(160deg, #0A1628 0%, #0F2447 45%, #13306B 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative glow blobs */}
      <Box
        sx={{
          position: 'absolute',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 65%)',
          top: -150,
          right: -150,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 65%)',
          bottom: 80,
          left: -100,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 65%)',
          bottom: '40%',
          right: '10%',
          pointerEvents: 'none',
        }}
      />

      {/* Top: Logo + brand */}
      <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
        <Logo sx={{ width: 44, height: 44 }} />
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, letterSpacing: -0.3 }}>
          YurMe CRM
        </Typography>
        <Chip
          label="Super Admin Console"
          size="small"
          sx={{
            alignSelf: 'flex-start',
            bgcolor: 'rgba(99,102,241,0.18)',
            color: '#A5B4FC',
            border: '1px solid rgba(99,102,241,0.35)',
            fontWeight: 600,
            fontSize: 11,
            height: 22,
          }}
        />
      </Stack>

      {/* Center: headline + features */}
      <Stack spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={1.5}>
          <Typography
            variant="h3"
            sx={{ color: 'white', fontWeight: 800, lineHeight: 1.25, letterSpacing: -0.5 }}
          >
            Complete control<br />over your platform
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', maxWidth: 320 }}>
            Monitor, manage, and configure every aspect of the YurMe ecosystem from one unified dashboard.
          </Typography>
        </Stack>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        <Stack spacing={2}>
          {FEATURES.map((f) => (
            <Stack key={f.label} direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(99,102,241,0.18)',
                  border: '1px solid rgba(99,102,241,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon={f.icon} width={17} sx={{ color: '#A5B4FC' }} />
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                {f.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {/* Bottom: copyright */}
      <Typography
        variant="caption"
        sx={{ color: 'rgba(255,255,255,0.25)', position: 'relative', zIndex: 1 }}
      >
        © {new Date().getFullYear()} YurMe. All rights reserved.
      </Typography>
    </Stack>
  );

  const renderContent = (
    <Stack
      sx={{
        width: 1,
        maxWidth: { xs: 1, md: 520 },
        flexShrink: 0,
        px: { xs: 3, sm: 6, md: 8 },
        py: { xs: 12, md: 0 },
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      {children}
    </Stack>
  );

  return (
    <Stack
      component="main"
      direction="row"
      sx={{ minHeight: '100vh' }}
    >
      {upMd && renderSection}
      {renderContent}
    </Stack>
  );
}

Layout.propTypes = {
  children: PropTypes.node,
  image: PropTypes.string,
  title: PropTypes.string,
};
